import { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Badge, Spinner } from '@components/ui';
import { useTasks, useSprints } from '@hooks';
import { dayKey, daysUntil, formatDay, localDayKey } from '@utils/calendarDate';

const DAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

export default function CalendarView({ projectId }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const { data: tasksData, isLoading: tasksLoading } = useTasks({ projectId }, { enabled: !!projectId });
  const { data: sprintsData, isLoading: sprintsLoading } = useSprints({ projectId }, { enabled: !!projectId });

  const tasks = tasksData?.data || [];
  const sprints = sprintsData?.data || [];

  const isLoading = tasksLoading || sprintsLoading;

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const goToPrevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get tasks for a specific date
  const getTasksForDate = (day) => {
    const dateStr = localDayKey(new Date(year, month, day));

    return tasks.filter(task => task.dueDate && dayKey(task.dueDate) === dateStr);
  };

  // Get sprint info for a date
  const getSprintForDate = (day) => {
    const dateStr = localDayKey(new Date(year, month, day));

    // 'YYYY-MM-DD' strings compare in calendar order; both ends are included.
    return sprints.find(sprint =>
      sprint.startDate && sprint.endDate &&
      dateStr >= dayKey(sprint.startDate) && dateStr <= dayKey(sprint.endDate)
    );
  };

  // Check if date is sprint start or end
  const isSprintBoundary = (day) => {
    const dateStr = localDayKey(new Date(year, month, day));

    return sprints.some(sprint => {
      const startStr = sprint.startDate ? dayKey(sprint.startDate) : null;
      const endStr = sprint.endDate ? dayKey(sprint.endDate) : null;
      return dateStr === startStr || dateStr === endStr;
    });
  };

  const today = new Date();
  const isToday = (day) => {
    return day === today.getDate() &&
           month === today.getMonth() &&
           year === today.getFullYear();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  // Generate calendar days
  const calendarDays = [];

  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null);
  }

  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-bold text-slate-800">Calendario</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={goToPrevMonth}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            ←
          </button>
          <button
            onClick={goToToday}
            className="px-3 py-1.5 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
          >
            Hoy
          </button>
          <button
            onClick={goToNextMonth}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            →
          </button>
        </div>
      </div>

      <Card>
        {/* Month header */}
        <div className="text-center mb-4">
          <h2 className="text-lg font-bold text-slate-800">
            {MONTHS[month]} {year}
          </h2>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {DAYS.map((day) => (
            <div
              key={day}
              className="text-center text-xs font-semibold text-slate-500 py-2"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((day, index) => {
            if (day === null) {
              return <div key={`empty-${index}`} className="h-24 bg-slate-50 rounded-lg" />;
            }

            const dayTasks = getTasksForDate(day);
            const sprint = getSprintForDate(day);
            const isBoundary = isSprintBoundary(day);

            return (
              <div
                key={day}
                className={`min-h-24 p-1.5 border rounded-lg transition-all ${
                  isToday(day)
                    ? 'border-indigo-500 bg-indigo-50'
                    : sprint
                    ? 'border-slate-200 bg-slate-50'
                    : 'border-slate-100 hover:border-slate-300'
                }`}
              >
                <div className="flex justify-between items-center mb-1">
                  <span
                    className={`text-sm font-medium ${
                      isToday(day)
                        ? 'text-indigo-600'
                        : 'text-slate-700'
                    }`}
                  >
                    {day}
                  </span>
                  {isBoundary && sprint && (
                    <span className="text-[10px] text-indigo-600 truncate max-w-[60px]">
                      {sprint.name}
                    </span>
                  )}
                </div>

                <div className="space-y-0.5">
                  {dayTasks.slice(0, 3).map((task) => (
                    <div
                      key={task.id}
                      className={`text-[10px] px-1 py-0.5 rounded truncate ${
                        task.priority === 'critical'
                          ? 'bg-red-100 text-red-700'
                          : task.priority === 'high'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-slate-200 text-slate-700'
                      }`}
                      title={task.title}
                    >
                      {task.title}
                    </div>
                  ))}
                  {dayTasks.length > 3 && (
                    <span className="text-[10px] text-slate-500">
                      +{dayTasks.length - 3} más
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Legend and upcoming */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <Card>
          <h3 className="font-semibold text-slate-800 mb-3">Sprints Activos</h3>
          <div className="space-y-2">
            {sprints.filter(s => s.status === 'active').map((sprint) => (
              <div key={sprint.id} className="flex justify-between items-center text-sm">
                <span className="text-slate-700">{sprint.name}</span>
                <span className="text-slate-500">
                  {sprint.startDate && formatDay(sprint.startDate)} - {sprint.endDate && formatDay(sprint.endDate)}
                </span>
              </div>
            ))}
            {sprints.filter(s => s.status === 'active').length === 0 && (
              <p className="text-sm text-slate-500">No hay sprints activos</p>
            )}
          </div>
        </Card>

        <Card>
          <h3 className="font-semibold text-slate-800 mb-3">Tareas Próximas</h3>
          <div className="space-y-2">
            {tasks
              .filter(t => t.dueDate && daysUntil(t.dueDate) >= 0)
              .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
              .slice(0, 5)
              .map((task) => (
                <div key={task.id} className="flex justify-between items-center text-sm">
                  <span className="text-slate-700 truncate max-w-[200px]">{task.title}</span>
                  <Badge variant={task.priority === 'critical' ? 'danger' : task.priority === 'high' ? 'warning' : 'slate'} size="sm">
                    {formatDay(task.dueDate)}
                  </Badge>
                </div>
              ))}
            {tasks.filter(t => t.dueDate && daysUntil(t.dueDate) >= 0).length === 0 && (
              <p className="text-sm text-slate-500">No hay tareas con fecha de entrega</p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

CalendarView.propTypes = {
  projectId: PropTypes.number,
};
